// utils/parseCsvData.ts

/**
 * A function to parse CSV data, find the email column, detect duplicates, etc.
 * @param data - The CSV data as an array of arrays (from Papa.parse results)
 * @returns An object with useful info such as the list of unique emails, duplicates, and errors.
 */
export function parseCsvData(data: string[][]) {
    if (!data || data.length === 0) {
        throw new Error("No data provided.");
    }

    const headers = data[0]; // The first row is typically headers
    const rows = data.slice(1);

    // Find the email column index (supports case-insensitive matching, e.g., "Email", "email")
    const emailColumnIndex = headers.findIndex((header) =>
        header.trim().toLowerCase() === "email"
    );

    if (emailColumnIndex === -1) {
        throw new Error("No 'email' column found in the CSV.");
    }

    // Collect emails and check duplicates
    const seen = new Set<string>();
    const duplicates: string[] = [];
    const validEmails: string[] = [];

    for (const row of rows) {
        // Make sure row has the correct number of columns
        if (row.length < headers.length) {
            // If row is shorter, handle it (skip, or consider it invalid)
            continue;
        }

        //if blank email, skip
        if (!row[emailColumnIndex] || row[emailColumnIndex].trim() === "") {
            continue;
        }

        const email = row[emailColumnIndex].trim();

        if (seen.has(email)) {
            duplicates.push(email);
        } else {
            seen.add(email);
            validEmails.push(email);
        }
    }

    // Return data for further processing:
    return {
        headers,
        rows,
        emailColumnIndex,
        validEmails,
        duplicates,
    };
}
